import React, { useEffect, useMemo, useState } from 'react'
import { localizedStrings } from '@core/strings'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Flex, Text } from '@fundamentals'
import {
  ProjectLocationPicker,
  ProjectPicker,
  ProjectUserPicker,
} from '@elements'
import { Button, Card, DialogActions, TextField } from '@common/material'
import { useGetEligibleParentsInfinite } from '@core/react-query/features/permits/hooks/useGetEligibleParentsInfinite'
import { usePermitById } from '@core/react-query/features/permits/permit'
import { ParentPermitField } from '@modules/template-form/ParentPermitField'
import { DatePicker } from '@common/material/DatePicker'
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar'
import { TemplateFormType } from '@modules/template-form/types'
import { useGetProjectLocations } from '@core/react-query/features/projects/locations'
import moment from 'moment-timezone'
import { useGetProjectPlan } from '@core/react-query/features/projects/plans'
import { useModal } from '@common/GlobalModal'
import { ProjectPlanPermitsModal } from 'webapp/components/modules/projectPlan/modals/ProjectPlanPermitsModal'
import MapIcon from '@mui/icons-material/Map'
import {
  getTemplateEndDateRules,
  getTemplateStartDateRules,
  MAX_SUMMARY_CHARACTERS,
} from '@core/components/templateAnswersForm'
import { RegisterItemPicker } from '@elements/Pickers/RegisterItemPicker'
import { AlertBox } from '@common/AlertBox'
import {
  ProjectMapGeoDrawAreaCard,
  ProjectMapGeoDrawAreaModal,
  W3WMapCard,
  W3WMapModal,
} from '@modules/geolocation'
import { PermissionEntityType } from '@core/entities/Groups/GroupsPermissions'
import { TemplateProjectLocations } from '@core/entities/template/TemplateProjectLocations/TemplateProjectLocations'
import { Permit } from '@core/entities/permit/Permit'
import { ProjectPlan } from '@core/entities/project/ProjectPlan'
import { ProjectLocationNode } from '@core/entities/project/ProjectLocationNode/ProjectLocationNode'
import { useGetProfile } from '@core/react-query/features/profile'
import { Profile } from '@core/entities/profile'

const StartsOnActionBar = (
  actionBarProps: PickersActionBarProps,
  onNowClick: () => void,
) => {
  const { onClear } = actionBarProps
  return (
    <DialogActions className='MuiPickersLayout-actionBar'>
      <Button onClick={onClear}>{localizedStrings.clear}</Button>
      <Button onClick={onNowClick}>{localizedStrings.now}</Button>
    </DialogActions>
  )
}

type BasicInformationProps = TemplateFormType & {
  organisationId: number
  permit?: Permit
  referenceObjectType?: PermissionEntityType
}

const BasicInformationForm: React.FC<BasicInformationProps> = ({
  organisationId,
  TemplatePicker,
  dateEnabled,
  w3wEnabled,
  projectPlanEnabled,
  multipleLocationsEnabled,
  fromRegister,
  fromRegisterInspection,
  isUsedForInspections,
  referenceObjectType,
  permit,
  parentPermitEnabled,
  templateLocked,
  parentPermitLocked,
}) => {
  const { showModal } = useModal()
  const { data: profile } = useGetProfile()
  const { isIsolated, companyIds } = Profile.getOrganisationIsolation(
    profile,
    organisationId,
  )
  const { getValues, watch, setValue, resetField, trigger } = useFormContext()
  const projectId = watch('projectId')
  const template = watch('template')
  const parentPermitId = watch('parentPermitId')
  const locations = watch('projectLocations.locations')
  const areas = watch('projectLocations.areas')
  const pins = watch('projectLocations.pins')
  const startsOn = watch('startsOn')
  const registerId = watch('registerId')
  const registerItemId = watch('registerItemId')

  const locationPlanId =
    locations?.[0]?.locationPlanId ||
    areas?.[0]?.locationPlan?.id ||
    areas?.[0]?.locationPlanId ||
    pins?.[0]?.planId ||
    pins?.[0]?.locationPlanId

  const locationHasPlan =
    !!projectPlanEnabled && !multipleLocationsEnabled && !!locationPlanId

  const isTemplateAuthorisedPerson =
    template?.currentVersion?.initiatorRole === 'AUTHORISED_PERSON'

  const w3wTemplate =
    template?.requireGeolocationForPermit &&
    template?.geoLocationInputType === 'WHAT_3_WORDS'

  useEffect(() => {
    if (!isTemplateAuthorisedPerson) resetField('permitHolderId')
    if (!w3wTemplate) resetField('coordinates.w3wLocation')
  }, [isTemplateAuthorisedPerson, w3wTemplate])

  const [{ draftId, permitHolder }] = useState(getValues())

  // Eligible parents are fetched here (and reused by the picker via the same
  // React Query cache key) so the form can read the selected parent's data
  // for field inheritance + date-range validation.
  const { data: eligibleParentsData } = useGetEligibleParentsInfinite(
    {
      organisationId,
      projectId,
      childTemplateId: template?.id,
      size: 20,
    },
    {
      enabled:
        !!parentPermitEnabled &&
        !!projectId &&
        !!template?.id &&
        !parentPermitLocked,
    },
  )

  const eligibleParents = useMemo(
    () => eligibleParentsData?.pages?.flat() ?? [],
    [eligibleParentsData],
  )

  // When the picker is locked (came via URL preselect), fetch the parent by id
  // directly — eligible-parents is paginated and won't necessarily contain it.
  const { data: lockedParentPermit } = usePermitById(
    { permitId: parentPermitId },
    { enabled: !!parentPermitId },
  )

  const selectedParent = useMemo(() => {
    if (lockedParentPermit) return lockedParentPermit
    if (!parentPermitId) return undefined
    return eligibleParents.find((p) => p.id === parentPermitId)
  }, [lockedParentPermit, eligibleParents, parentPermitId])

  // Default location + plan zones from the parent on selection; leave them
  // editable so the child can diverge. Dates aren't inherited — parent ranges
  // are typically much wider than child max-duration, so we let the user pick
  // fresh dates. Keyed on `selectedParent?.id` so the effect fires on actual
  // parent transitions and not on every reference change.
  useEffect(() => {
    if (!selectedParent) return

    const parentProjectLocations = selectedParent.projectLocations
    if (parentProjectLocations?.locations?.length) {
      setValue('projectLocations.locations', parentProjectLocations.locations, {
        shouldDirty: true,
      })
    }
    // Always set areas/pins so switching from a plan-equipped parent to one
    // without plan zones clears stale data instead of leaking through.
    setValue('projectLocations.areas', parentProjectLocations?.areas ?? [], {
      shouldDirty: true,
    })
    setValue('projectLocations.pins', parentProjectLocations?.pins ?? [], {
      shouldDirty: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParent?.id])

  const { data: projectLocations } = useGetProjectLocations(
    {
      projectId,
      statuses: ['ACTIVE'],
    },
    {
      refetchOnMount: false,
    },
  )

  const selectedLocationNodeId = locations?.[0]?.locationNodeId

  const initialViewState = useMemo(() => {
    if (!selectedLocationNodeId || !projectLocations?.locations) return null
    const boundary = ProjectLocationNode.resolveLocationBoundary(
      selectedLocationNodeId,
      projectLocations.locations,
    )
    return boundary?.toInitialViewState()
  }, [selectedLocationNodeId, projectLocations?.locations])

  const what3WordsRegex =
    /^\/{0,}[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}$/

  const openProjectPlan = (plan: ProjectPlan) => {
    if (plan.isPublished() && plan.areas.length) {
      showModal(
        <ProjectPlanPermitsModal
          nodeId={locations?.[0]?.locationNodeId}
          projectId={projectId}
          planId={plan.id}
          nodePath={locations?.[0]?.locationName}
          onSuccess={(areas, pins) => {
            const areasValue =
              areas.length || !pins.length
                ? [
                    {
                      locationPlanId: plan.id,
                      selectedAreas: areas.map((uuid) => ({ uuid })),
                    },
                  ]
                : []
            const pinsValue = pins?.length
              ? [{ droppedPins: pins, locationPlanId: plan.id }]
              : []

            setValue('projectLocations.areas', areasValue, {
              shouldDirty: true,
            })
            setValue('projectLocations.pins', pinsValue, {
              shouldDirty: true,
            })
          }}
          initialSelectedAreas={areas?.[0]?.selectedAreas.map(
            ({ uuid }) => uuid,
          )}
          initialSelectedPins={pins?.[0]?.droppedPins ?? []}
          plan={plan}
          permit={permit}
          enablePinCreation={true}
        />,
      )
    }
  }

  const { data: projectPlan } = useGetProjectPlan(
    {
      projectId,
      nodeId: locations?.[0]?.locationNodeId,
      planId: locationPlanId,
    },
    {
      enabled: locationHasPlan,
      refetchOnMount: false,
      onSuccess: openProjectPlan,
    },
  )

  const registerPickerBeforeChecklist = fromRegister
  const registerItemPicker = (
    <Controller
      name='registerItemId'
      render={({ field, fieldState }) => {
        return (
          <RegisterItemPicker
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ mt: 2 }}
            disabled={!!draftId || fromRegister}
            registerId={registerId}
            fullWidth
            fromInspection={fromRegisterInspection}
            {...field}
          />
        )
      }}
    />
  )

  // Project is locked to the parent's (a child must live in the same project).
  // Location and plan zones default to the parent but stay editable. Dates use
  // the form's own defaults — parent ranges are typically much wider than a
  // child's max-duration window.
  const projectLockedToParent = !!selectedParent

  return (
    <Card sx={{ p: 2 }}>
      <Controller
        name='projectId'
        rules={{ required: localizedStrings.selectProject }}
        render={({ field: { value, onChange, ...field }, fieldState }) => {
          return (
            <ProjectPicker
              organisationId={organisationId}
              value={value}
              onChange={(e) => {
                setValue('template', null)
                if (!parentPermitLocked) {
                  setValue('parentPermitId', undefined)
                }
                onChange(e)
              }}
              disabled={!!draftId || fromRegister || projectLockedToParent}
              textFieldProps={{
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
                fullWidth: true,
                ...field,
              }}
            />
          )
        }}
      />

      {registerId && registerPickerBeforeChecklist && registerItemPicker}

      {projectId && (
        <Controller
          name='template'
          rules={{ required: localizedStrings.selectTemplate }}
          render={({ field: { onChange, ...field }, fieldState }) => {
            return (
              <TemplatePicker
                organisationId={organisationId}
                projectId={projectId}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ mt: 2 }}
                disabled={!!draftId || fromRegisterInspection || templateLocked}
                registerId={registerId}
                registerItemId={registerItemId}
                fromRegisterInspection={fromRegisterInspection}
                isUsedForInspections={isUsedForInspections}
                fullWidth
                onChange={(nextTemplate) => {
                  // Drop the previously picked parent — eligibility depends on
                  // the chosen template, so the old selection may not apply
                  // (and would otherwise keep the picker mounted with stale
                  // state until the user clears it manually).
                  if (!parentPermitLocked) {
                    setValue('parentPermitId', undefined)
                  }
                  onChange(nextTemplate)
                }}
                {...field}
              />
            )
          }}
        />
      )}

      {registerId && !registerPickerBeforeChecklist && registerItemPicker}

      {parentPermitEnabled && projectId && template && (
        <ParentPermitField
          organisationId={organisationId}
          projectId={projectId}
          childTemplateId={template.id}
          initialParentPermitId={parentPermitId}
          readOnly={parentPermitLocked}
          selectedPermit={selectedParent}
          requireParentPermit={template.requireParentPermit}
        />
      )}

      {isTemplateAuthorisedPerson && (
        <Controller
          name='permitHolderId'
          rules={{ required: localizedStrings.pleaseSelectAnAssignee }}
          render={({ field, fieldState }) => {
            return (
              <ProjectUserPicker
                projectId={projectId}
                permitHolder={permitHolder}
                allowSelf={true}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ mt: 2 }}
                fullWidth
                setPermitHolder={setValue}
                referenceObjectId={template?.id}
                referenceObjectType={referenceObjectType}
                companyIds={isIsolated ? companyIds?.map(String) : undefined}
                {...field}
              />
            )
          }}
        />
      )}

      {projectId && projectLocations?.enabled && (
        <Controller
          name='projectLocations.locations'
          rules={{
            required: localizedStrings.selectLocation,
            validate: (value) =>
              !!value?.filter((l) => l.locationNodeId).length,
          }}
          render={({ field: { onChange, ...field }, fieldState }) => {
            return (
              <Box mt={2}>
                <ProjectLocationPicker
                  projectId={projectId}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={(locations) => {
                    resetField('projectLocations')
                    onChange(locations)
                  }}
                  {...field}
                />

                <Flex>
                  {projectPlanEnabled &&
                    projectPlan &&
                    !!projectPlan.areas?.length && (
                      <Controller
                        name='projectLocations'
                        rules={{
                          required: localizedStrings.selectZones,
                          validate: (value: TemplateProjectLocations) => {
                            return (
                              !!value?.areas?.[0]?.selectedAreas?.length ||
                              !!value?.pins?.[0]?.droppedPins?.length
                            )
                          },
                        }}
                        render={({ field, fieldState }) => {
                          const selectedZones =
                            field.value?.areas?.[0]?.selectedAreas
                          const pins = field.value?.pins?.[0]?.droppedPins

                          return (
                            <Button
                              sx={{ mt: 1 }}
                              startIcon={<MapIcon />}
                              color={fieldState.error ? 'error' : undefined}
                              onClick={() => openProjectPlan(projectPlan)}
                            >
                              <Text>
                                {projectPlan.getHumanReadableValueString(
                                  selectedZones?.map(({ uuid }) => uuid) || [],
                                  pins,
                                )}
                              </Text>
                            </Button>
                          )
                        }}
                      />
                    )}
                </Flex>
              </Box>
            )
          }}
        />
      )}

      {projectId && !projectLocations?.enabled && (
        <Controller
          name='projectLocations.locations'
          rules={{ validate: (value) => !!value?.[0]?.locationName }}
          render={({ field: { onChange, value, ...field }, fieldState }) => (
            <TextField
              label={localizedStrings.location}
              value={value?.[0]?.locationName ?? ''}
              placeholder={localizedStrings.location}
              data-test='select-location'
              onChange={(e) => {
                const locationName = e.target.value
                onChange(locationName ? [{ locationName }] : null)
              }}
              sx={{ mt: 2 }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              FormHelperTextProps={{
                error: !!fieldState.error,
                sx: { pt: 0.5, pb: 2 },
              }}
              // Placeholder + label both render in the input area; without
              // shrinking the label they overlap whenever the value is empty
              // or set programmatically (e.g. inherited from parent).
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              {...field}
            />
          )}
        />
      )}

      {template?.requireGeolocationForPermit &&
        template?.geoLocationInputType === 'DRAW_ON_MAP' && (
          <Controller
            name={'geoLocationAreas'}
            rules={{ required: localizedStrings.drawOnMapRequiredErrorMessage }}
            render={({ field, fieldState }) => {
              return (
                <ProjectMapGeoDrawAreaCard
                  projectId={projectId}
                  geoLocationAreas={field.value}
                  fieldState={fieldState}
                  onClick={() => {
                    showModal(
                      <ProjectMapGeoDrawAreaModal
                        projectId={projectId}
                        organisationId={organisationId}
                        initialGeoLocationAreas={field.value}
                        initialViewState={initialViewState}
                        onChangeGeoLocationAreas={(geoLocationAreas) => {
                          field.onChange(geoLocationAreas)
                        }}
                      />,
                    )
                  }}
                />
              )
            }}
          />
        )}

      {template?.requireGeolocationForChecklist &&
        template?.geoLocationInputTypes?.includes('DEVICE_LOCATION') && (
          <Controller
            name='coordinates'
            rules={{ required: true }}
            render={() => {
              return (
                <AlertBox type='error' mt={2}>
                  {localizedStrings.completeAtPointOfWorks}
                </AlertBox>
              )
            }}
          />
        )}

      {w3wEnabled &&
        template?.requireGeolocationForPermit &&
        template?.geoLocationInputType === 'WHAT_3_WORDS' && (
          <Controller
            name='coordinates.w3wLocation'
            rules={{
              validate: (value) =>
                what3WordsRegex.test(value) ||
                'Please enter valid what3words location',
            }}
            render={({ field, fieldState }) => {
              return (
                <W3WMapCard
                  what3words={field.value}
                  fieldState={fieldState}
                  onClick={() => {
                    showModal(
                      <W3WMapModal
                        projectId={projectId}
                        organisationId={organisationId}
                        initialWhat3Words={field.value}
                        initialViewState={initialViewState}
                        onChangeWhat3Words={(what3words) => {
                          field.onChange(what3words)
                        }}
                      />,
                    )
                  }}
                />
              )
            }}
          />
        )}

      {dateEnabled && template && (
        <Flex mt={2}>
          <Controller
            name='startsOn'
            rules={getTemplateStartDateRules(selectedParent)}
            render={({ field: { value, onChange, ...field }, fieldState }) => {
              return (
                <DatePicker
                  dataTest='date-picker-start-date'
                  value={value ? moment(value) : undefined}
                  onChange={(rawDate) => {
                    if (!rawDate) {
                      onChange(undefined)
                      return
                    }
                    const processedDate = template?.currentVersion
                      .calculateDurationBasedOnDays
                      ? rawDate.set({
                          hour: 0,
                          minute: 1,
                          second: 0,
                          millisecond: 0,
                        })
                      : rawDate
                    onChange(processedDate.toISOString())
                    setTimeout(() => trigger('endsOn'), 500)
                  }}
                  withTime={
                    !template?.currentVersion.calculateDurationBasedOnDays
                  }
                  showInputTime
                  label={localizedStrings.starts}
                  error={!!fieldState.error}
                  sx={{ flex: 1, mr: 2 }}
                  required
                  disablePast
                  slots={{
                    actionBar: (props) =>
                      StartsOnActionBar(props, () => {
                        onChange(moment().toISOString())
                      }),
                  }}
                  {...field}
                />
              )
            }}
          />

          <Controller
            name='endsOn'
            rules={getTemplateEndDateRules(
              startsOn,
              template?.currentVersion,
              selectedParent,
            )}
            render={({ field: { value, onChange, ...field }, fieldState }) => {
              return (
                <DatePicker
                  dataTest='date-picker-end-date'
                  value={value ? moment(value) : undefined}
                  onChange={(rawDate) => {
                    if (!rawDate) {
                      onChange(undefined)
                      return
                    }

                    const processedDate = template?.currentVersion
                      .calculateDurationBasedOnDays
                      ? rawDate.set({
                          hour: 23,
                          minute: 59,
                          second: 0,
                          millisecond: 0,
                        })
                      : rawDate
                    onChange(processedDate.toISOString())
                  }}
                  withTime={
                    !template?.currentVersion.calculateDurationBasedOnDays
                  }
                  showInputTime
                  label={localizedStrings.ends}
                  error={!!fieldState.error}
                  disablePast
                  slotProps={{
                    textField: {
                      helperText: fieldState.error?.message,
                      FormHelperTextProps: { sx: { pt: 0.5, pb: 2 } },
                    },
                  }}
                  sx={{ flex: 1 }}
                  required
                  {...field}
                />
              )
            }}
          />
        </Flex>
      )}

      <Controller
        name='summary'
        rules={{
          required: fromRegisterInspection
            ? false
            : localizedStrings.pleaseEnterASummary,
        }}
        render={({ field: { onChange, ...field }, fieldState }) => {
          return (
            <TextField
              label={localizedStrings.summary}
              disabled={!projectId}
              placeholder={localizedStrings.permitSummaryPlaceholder}
              data-test='permit-summary-input'
              rows={3}
              sx={{ mt: 2 }}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ||
                `${
                  field.value?.length || 0
                }/${MAX_SUMMARY_CHARACTERS} characters`
              }
              onChange={(e) =>
                e.target.value.length <= MAX_SUMMARY_CHARACTERS && onChange(e)
              }
              FormHelperTextProps={{
                error: !!fieldState.error,
                sx: { pt: 0.5, pb: 2 },
              }}
              fullWidth
              required
              multiline
              {...field}
            />
          )
        }}
      />
    </Card>
  )
}

export { BasicInformationForm }
